import React, { useState, useEffect } from 'react';

import { useOutletContext, useNavigate } from "react-router-dom";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";

import { Grid, Box, Typography, } from "@mui/material";

import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import { SolidButton } from "components/buttons/SolidButton";
import ModalContainer from 'components/ModalContainer';

import ModalforCheck from '../modalCheck.js';
import ModalForAddOrgOrSite from '../ModalForAddOrgOrSite.js';

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

import { mergeRealmData } from "utils/getHierarchy";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const Realm = () => {
  const {scopeData, setScopeData} = useOutletContext();
  
  const { setBread } = useBreadStorageContext();

  const [realmListUuid, _setRealmListUuid] = useState(uuidv4());
  const [nowUuid, setNowUuid] = useState('');
  
  const { dataMap, sendRequest } = useDataSourceManager();
  
  const [openModalForAddOrg, setOpenModalForAddOrg] = useState(false);
  const [modalForAddOrgTitle, setModalForAddOrgTitle] = useState('');
  const [modalForAddOrgCancelBtn, setModalForAddOrgCancelBtn] = useState('');
  const [modalForAddOrgSaveBtn, setModalForAddOrgSaveBtn] = useState('');

  const [openModalCheck, setOpenModalCheck] = useState(false);
  const [modalCheckTitle, setModalCheckTitle] = useState('');
  const [modalCheckContent, setModalCheckContent] = useState('');
  const [modalCheckCancelBtn, setModalCheckCancelBtn] = useState('');
  const [modalCheckSaveBtn, setModalCheckSaveBtn] = useState('');

  const [modalFrom, setModalFrom] = useState('');
  const [modalAction, setModalAction] = useState(false);
  const [updateItem, setUpdateItem] = useState({});

  const getDatas = async () => {
    setNowUuid(realmListUuid)
    let tmpResource = '/me/memberships.read';
    sendRequest( tmpResource, realmListUuid);
  };

  const handleAddItem = () =>{
    setModalFrom('Add')
    let newItem = {
      id:'', name: '', hasManageOrg: false, 
      roleId: '',roleName: '',
      createdAt: '', createdTime: ''
    }
    setUpdateItem(newItem)
    
    setModalForAddOrgTitle('Add Organization')
    setModalForAddOrgCancelBtn('Cancel')
    setModalForAddOrgSaveBtn('Save')
    setOpenModalForAddOrg(true)
  }

  const handleScopeLink = (item) =>{
    // save storage
    let tmpSelectedBread = {
      realm: item,
      orgs: {}, site: {},
    }

    localStorage.setItem('selectBreadcrumbs', JSON.stringify(tmpSelectedBread));
    setBread(tmpSelectedBread)
    // navigate
    navigatePage(item);
  }

  const navigate = useNavigate();
  const navigatePage = (item) => {
    let path = "";
    if(item.hasManageOrg === false){
      path = "/org/sites";
    }else{
      path = "/msp/organizations";
    }
    navigate(path);
  }

  function handleActionDenied(message) {
    setOpenModalForAddOrg(false);
    setModalCheckTitle('Action denied')
    setModalCheckContent(message)
    setModalCheckCancelBtn('')
    setModalCheckSaveBtn('OK')
    setModalFrom('error')
    setOpenModalCheck(true)
  }

  useEffect(() => {
    if(nowUuid !== ''){
      switch (nowUuid) {
        case realmListUuid:
          if(dataMap[realmListUuid]?.code !== undefined){
            setModalCheckTitle('Action denied')
            setModalCheckContent(dataMap[realmListUuid].message)
            setModalCheckCancelBtn('')
            setModalCheckSaveBtn('OK')
            setModalFrom('error')
            setOpenModalCheck(true)
          }else if(dataMap[realmListUuid] !== undefined){
            let tmpEntData = { data:[] }
            let tmpAloneOrgData = { data:[] }
            if(dataMap[realmListUuid].data.enterprises && dataMap[realmListUuid].data.enterprises.length > 0){
              tmpEntData.data = dataMap[realmListUuid].data.enterprises
            }
            if(dataMap[realmListUuid].data.standaloneOrgs && dataMap[realmListUuid].data.standaloneOrgs.length > 0){
              tmpAloneOrgData.data = dataMap[realmListUuid].data.standaloneOrgs
            }

            const result = mergeRealmData(tmpEntData, tmpAloneOrgData)
            setScopeData(prev => ({
              ...prev,
              realm: result
            }));
          }

          setNowUuid('')
          break;
        default:
          break;
      }
    }
  }, [dataMap]);

  useEffect(() => {
    if(modalAction === true){
      if(modalFrom === 'Add'){
        if(checkMockup === 'true'){
          let addItem = cloneDeep(updateItem)
          addItem.id = uuidv4();
          
          setScopeData(prev => ({
            ...prev,
            realm: [...prev.realm, addItem]
          }));
        }
      }
      setModalAction(false)
    }
  }, [modalAction]);

  const columnHelper = createColumnHelper();
  const columnsData = [
    columnHelper.accessor("name", {
      header: "Organization",
      cell: (info) => (
        <div onClick={() => handleScopeLink(info.row.original)} style={{color:'#707ca8', cursor:'pointer', }}>
          <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', textDecoration:'underline',}}>
            {info.getValue()}
            </span>
        </div>
      ),
    }),
    columnHelper.accessor("roleName", {
      header: "Role",
    }),
    columnHelper.accessor("createdTime", {
      header: "Created",
    }),
  ];

  const table = useTableInstance({
    tableName: "hierarchyRealmMsp",
    data: scopeData.realm,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  const emptyHead = useTableInstance({
    tableName: "emptyRealmMsp",
    data: '',
    columns: columnsData,
    enableSorting: true,
  });

  return (
      <>
        <div className={`main-card-content`}
          style={{marginTop: '60px', flex: (scopeData.realm.length>0)?'':'1', 
          display:'flex', flexDirection:'column'}}>
          <div>
            <Grid container style={{justifyContent:'space-between', marginBottom:'10px'}}>
              <Box style={{display:'flex'}}>
                <Grid container sx={{alignItems: "center",}}>
                  <Typography fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                    Please select your Organization, or create a new one.
                  </Typography>
                </Grid>
              </Box>
              <Box style={{display:'flex'}}>
                { scopeData.realm.length>0 && <Grid container sx={{alignItems: "center", gap:'15px'}}>
                  <SearchInput
                    value={table.globalFilter}
                    onChange={table.setGlobalFilter}
                  />
                  <SolidButton
                    startIcon={<AddIcon />}
                    height="32px"
                    onClick={() => handleAddItem()}
                  >
                    Add Organization
                  </SolidButton>
                </Grid> }
              </Box>
            </Grid>
          </div>
          {scopeData.realm.length > 0 && 
            <TableField 
              {...table} 
            />
          }
          { scopeData.realm.length === 0 && <>
            <TableField
              {...emptyHead}
            />    
            <div style={{flex:'1',alignContent:'center', justifyItems:'center'}}>
              <Grid container style={{justifyContent:'center', marginBottom:'40px'}}>
                <Typography sx={{textAlign:'center'}}
                fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                  No entry in the table, you can create a new organization.
                </Typography>
              </Grid>
              <SolidButton
                startIcon={<AddIcon />}
                height="32px"
                onClick={() => handleAddItem()}
              >
                Add Organization
              </SolidButton>
            </div> 
          </>}
        </div>
        <ModalContainer
          display={openModalCheck}
          modalWidth={'500px'} //550px, 750px, empty=1000px,
          children={
            <ModalforCheck
              title={modalCheckTitle}
              content={modalCheckContent}
              cancelBtn={modalCheckCancelBtn}
              saveBtn={modalCheckSaveBtn}
              setOpenModal={setOpenModalCheck}
              setModalAction={setModalAction}
            />
          }
        />
        <ModalForAddOrgOrSite
          auth="Organization"
          display={openModalForAddOrg}
          title={modalForAddOrgTitle}
          setOpenModal={setOpenModalForAddOrg}
          cancelBtn={modalForAddOrgCancelBtn}
          saveBtn={modalForAddOrgSaveBtn}
          errorHandling={handleActionDenied}
          refresh={getDatas}
        />
      </>
  );
};

export default Realm;